import React, { useMemo } from 'react';
import { Box, Field, FieldStateTypes } from '@common-fe/common-fe';
import _ from 'lodash';

import { EMPTY_LABEL_SIGN } from '@/common/constants';

import { PlanStatuses } from '../../plan.types';
import { PLAN_SETUP_FIELD } from '../PlanSetup.constants';
import TemplateStateField from '../TemplateStateField';

import { useCurrentPlan } from './useCurrentPlan';
import useParentId from './useParentId';
import { useActiveUpdateFields } from './usePlanFieldsState';
import usePlanSetupFieldsSetter from './usePlanSetupFieldsSetter';
import usePlanSetupFieldsSourceState from './usePlanSetupFieldsSourceState';
import usePlanSetupFieldsState from './usePlanSetupFieldsState';

const PRE_LABEL_NODE_DEFAULT_WIDTH = '56px';
const BLOCKED_FIELDS = [PLAN_SETUP_FIELD.PLAN_TEMPLATE, PLAN_SETUP_FIELD.PLAN_TYPE];

const useTemplateFieldState = (fields: Field[], isTemplateMode?: boolean, isEditMode?: boolean) => {
  const parentId = useParentId();
  const isParentIdExists = useMemo(() => !!parentId, [parentId]);
  const sourceState = usePlanSetupFieldsSourceState();
  const state = usePlanSetupFieldsState();
  const { planStatus } = useCurrentPlan();

  const { handleSetState } = usePlanSetupFieldsSetter();
  const activeUpdateFields = useActiveUpdateFields();

  const isMinimized = useMemo(() => !isTemplateMode || !isEditMode, [isEditMode, isTemplateMode]);
  const isActivePlanTemplate = useMemo(
    () =>
      !!isTemplateMode &&
      (planStatus === PlanStatuses.ACTIVE ||
        planStatus === PlanStatuses.PENDING_ACTIVE ||
        planStatus === PlanStatuses.UNDER_UPDATE),
    [isTemplateMode, planStatus]
  );

  const getModifiedFields = (currentFields: Field[]): Field[] =>
    currentFields.map((field) => {
      const currentValue = _.get(state, `${field.name}`) as FieldStateTypes;
      const sourceValue = _.get(sourceState, `${field.name}`) as FieldStateTypes;
      const sourceDisabled = sourceValue === FieldStateTypes.Locked || sourceValue === FieldStateTypes.Hidden;
      const disabledByActiveTemplate = isActivePlanTemplate && !activeUpdateFields.includes(field.name);
      const appliedDisabling =
        field.name === PLAN_SETUP_FIELD.PLAN_SUMMARY ||
        disabledByActiveTemplate ||
        (isParentIdExists ? sourceDisabled : BLOCKED_FIELDS.includes(field.name) && sourceDisabled);
      if (field.label === EMPTY_LABEL_SIGN) {
        return {
          ...field,
          subFields: field.subFields ? getModifiedFields(field.subFields) : undefined,
          preLabelNode: <Box width={{ min: PRE_LABEL_NODE_DEFAULT_WIDTH }} />,
        };
      }
      if (field.subFields && field.label) {
        return {
          ...field,
          subFields: getModifiedFields(field.subFields),
          preLabelNode: (
            <Box width={{ min: PRE_LABEL_NODE_DEFAULT_WIDTH }}>
              <TemplateStateField
                testId={`${field.name}_state`}
                minimized={isMinimized}
                disabled={appliedDisabling}
                value={currentValue}
                onChangeValue={(value) => {
                  // Sync perPersonSpendLimit & perCategorySpendLimit with totalPlanLimit
                  // since all three share a single template state controlled by totalPlanLimit
                  const update: Record<string, FieldStateTypes> = { [field.name]: value };
                  if (field.name === PLAN_SETUP_FIELD.TOTAL_PLAN_LIMIT) {
                    update[PLAN_SETUP_FIELD.PER_PERSON_SPEND_LIMIT] = value;
                    update[PLAN_SETUP_FIELD.PER_CATEGORY_SPEND_LIMIT] = value;
                  }
                  handleSetState(update);
                }}
              />
            </Box>
          ),
        };
      }

      if (field.label) {
        return {
          ...field,

          preLabelNode: (
            <Box width={{ min: PRE_LABEL_NODE_DEFAULT_WIDTH }}>
              <TemplateStateField
                testId={`${field.name}_state`}
                minimized={isMinimized}
                value={currentValue}
                disabled={appliedDisabling}
                onChangeValue={(value) => handleSetState({ [field.name]: value })}
              />
            </Box>
          ),
        };
      }
      return {
        ...field,
        preLabelNode: <Box width={PRE_LABEL_NODE_DEFAULT_WIDTH} />,
      };
    });

  return getModifiedFields(fields);
};

export default useTemplateFieldState;
